import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { AppBar } from './ui/app-bar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from './ui/pagination';
import { ChevronLeft, Edit, Trash2, Plus, Loader2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from './ui/use-toast';
import { getBaseUrl } from '@/lib/api';
import { getAuthToken } from '@/lib/authToken';
import { useSettings } from '@/hooks/useSettings';

export interface LocationRecord {
  ID?: number | string;
  Id?: number | string;
  Name: string;
  Type?: string;           // api/Locations: location type (e.g. "Warehouse")
  LocationType?: string;
  TypeDesc?: string;
  Address?: string;
  ContactPerson?: string;
  Phone?: string;
  ContactPersonNumber?: string;
  state?: string;          // api/Locations: lowercase state
  State?: string;
  CompanyName?: string;    // api/Locations: company display name
  Company?: string;
  CompanyID?: number | string;
  Active?: boolean;
}

const LOCATION_TYPES = ['Head Office', 'Branch', 'Warehouse'] as const;

interface LocationManagementScreenProps {
  token: string;
  onBack: () => void;
}

const authHeader = (): string => {
  const t = getAuthToken();
  return t && !t.startsWith('Bearer ') ? `Bearer ${t}` : t || '';
};

export default function LocationManagementScreen({ token, onBack }: LocationManagementScreenProps) {
  const queryClient = useQueryClient();
  const baseUrl = getBaseUrl();
  const authToken = authHeader() || (token.startsWith('Bearer ') ? token : `Bearer ${token}`);
  const { primaryCompanyId, getSetting } = useSettings();
  const locationNameSetting = (getSetting('Location Name') || '').trim();
  const locationDisplayName = locationNameSetting || 'Locations';
  const locationFieldLabel = locationNameSetting || 'Location Name';
  const locationTypeLabel = locationNameSetting ? `${locationNameSetting} Type` : 'Location Type';

  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState<keyof LocationRecord>('Name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<LocationRecord | null>(null);
  const [editingLocation, setEditingLocation] = useState<LocationRecord | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [form, setForm] = useState({
    Name: '',
    LocationType: 'Branch',
    Address: '',
    ContactPerson: '',
    Phone: '',
    State: '',
    CompanyID: '',
  });

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const [mobileView, setMobileView] = useState(isMobile);

  useEffect(() => {
    const onResize = () => setMobileView(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { data: locationsData, isLoading } = useQuery({
    queryKey: ['locations-master'],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/api/Locations`, {
        headers: { Authorization: authToken, Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to fetch locations');
      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data : json?.data?.data ?? json ?? [];
      return list as LocationRecord[];
    },
  });

  const { data: statesData } = useQuery({
    queryKey: ['states'],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/api/States`, {
        headers: { Authorization: authToken, Accept: 'application/json' },
      });
      if (!res.ok) return [];
      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data : json?.data?.data ?? [];
      return list as { State: string; Code?: string }[];
    },
  });

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/api/Companies`, {
        headers: { Authorization: authToken, Accept: 'application/json' },
      });
      if (!res.ok) return [];
      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data : json?.data?.data ?? [];
      return list as { Id?: number; CompanyID?: number; Name?: string; State?: string }[];
    },
  });

  const locations = locationsData ?? [];
  const states = statesData ?? [];
  const companies = companiesData ?? [];

  const filtered = useMemo(() => {
    let list = locations;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((l) => (l.Name || '').toLowerCase().includes(q));
    }
    return list;
  }, [locations, searchQuery]);

  const getSortValue = (loc: LocationRecord, key: keyof LocationRecord): string | number | boolean => {
    if (key === 'LocationType') return loc.Type ?? loc.TypeDesc ?? loc.LocationType ?? '';
    if (key === 'Phone') return loc.ContactPersonNumber ?? loc.Phone ?? '';
    if (key === 'State') return loc.state ?? loc.State ?? '';
    if (key === 'Company') return loc.CompanyName ?? loc.Company ?? '';
    return loc[key] ?? '';
  };

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = getSortValue(a, sortKey);
      const bVal = getSortValue(b, sortKey);
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, currentPage, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const toggleSort = (key: keyof LocationRecord) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ column }: { column: keyof LocationRecord }) => {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 h-4 w-4 inline opacity-50" />;
    return sortDir === 'asc' ? <ArrowUp className="ml-1 h-4 w-4 inline" /> : <ArrowDown className="ml-1 h-4 w-4 inline" />;
  };

  const resetForm = () => {
    setForm({
      Name: '',
      LocationType: 'Branch',
      Address: '',
      ContactPerson: '',
      Phone: '',
      State: '',
      CompanyID: '',
    });
    setEditingLocation(null);
  };

  const openEdit = (loc: LocationRecord) => {
    setEditingLocation(loc);
    setForm({
      Name: loc.Name || '',
      LocationType: (loc.Type ?? loc.TypeDesc ?? loc.LocationType) as string || 'Branch',
      Address: loc.Address || '',
      ContactPerson: loc.ContactPerson || '',
      Phone: loc.ContactPersonNumber ?? loc.Phone ?? '',
      State: loc.state ?? loc.State ?? '',
      CompanyID: loc.CompanyID != null ? String(loc.CompanyID) : '',
    });
    setEditModalOpen(true);
  };

  const validatePhone = (v: string) => /^[0-9+\-\s]*$/.test(v);

  const handleAdd = async () => {
    if (!form.Name.trim()) {
      toast({ title: 'Validation', description: `${locationFieldLabel} is required.`, variant: 'destructive' });
      return;
    }
    if (form.Phone && !validatePhone(form.Phone)) {
      toast({ title: 'Validation', description: 'Phone must contain only numbers.', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        Id: 0,
        Name: form.Name.trim(),
        TypeDesc: form.LocationType,
        Address: form.Address.trim(),
        ContactPerson: form.ContactPerson.trim(),
        ContactPersonNumber: form.Phone.trim(),
        State: form.State || undefined,
        CompanyID: form.CompanyID ? Number(form.CompanyID) : 1,
        Active: true,
      };
      const apiUrl = `${baseUrl}/api/SaveLocation`;
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authToken },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.status === -1) {
        console.log('[SaveLocation POST - Add] API:', apiUrl, 'Payload:', JSON.stringify(payload), 'Response:', data);
        toast({ title: 'Error', description: data?.message || 'Failed to add location.', variant: 'destructive' });
      } else if (res.ok || data?.status === 0) {
        console.log('[SaveLocation POST - Add] API:', apiUrl, 'Payload:', JSON.stringify(payload), 'Response:', data);
        toast({ title: 'Success', description: 'Location added successfully.' });
        resetForm();
        setAddModalOpen(false);
        queryClient.invalidateQueries({ queryKey: ['locations-master'] });
      } else {
        console.log('[SaveLocation POST - Add] API:', apiUrl, 'Payload:', JSON.stringify(payload), 'Response:', data);
        toast({ title: 'Error', description: data?.message || 'Failed to add location.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed to add location.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openAddModal = () => {
    const companyId = primaryCompanyId || '';
    const company = companies.find((c) => String(c.Id ?? c.CompanyID ?? '') === companyId);
    const defaultState = company?.State ?? '';
    setForm({
      Name: '',
      LocationType: 'Branch',
      Address: '',
      ContactPerson: '',
      Phone: '',
      State: defaultState,
      CompanyID: companyId,
    });
    setEditingLocation(null);
    setAddModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingLocation || !form.Name.trim()) {
      toast({ title: 'Validation', description: `${locationFieldLabel} is required.`, variant: 'destructive' });
      return;
    }
    if (form.Phone && !validatePhone(form.Phone)) {
      toast({ title: 'Validation', description: 'Phone must contain only numbers.', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    try {
      const id = Number(editingLocation.ID ?? editingLocation.Id ?? 0);
      const payload = {
        Id: id,
        Name: form.Name.trim(),
        TypeDesc: form.LocationType,
        Address: form.Address.trim(),
        ContactPerson: form.ContactPerson.trim(),
        ContactPersonNumber: form.Phone.trim(),
        State: form.State || undefined,
        CompanyID: form.CompanyID ? Number(form.CompanyID) : 1,
        Active: editingLocation.Active ?? true,
      };
      const apiUrl = `${baseUrl}/api/SaveLocation`;
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authToken },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.status === -1) {
        console.log('[SaveLocation POST - Update] API:', apiUrl, 'Payload:', JSON.stringify(payload), 'Response:', data);
        toast({ title: 'Error', description: data?.message || 'Failed to update location.', variant: 'destructive' });
      } else if (res.ok || data?.status === 0) {
        console.log('[SaveLocation POST - Update] API:', apiUrl, 'Payload:', JSON.stringify(payload), 'Response:', data);
        toast({ title: 'Success', description: 'Location updated successfully.' });
        setEditModalOpen(false);
        resetForm();
        queryClient.invalidateQueries({ queryKey: ['locations-master'] });
      } else {
        console.log('[SaveLocation POST - Update] API:', apiUrl, 'Payload:', JSON.stringify(payload), 'Response:', data);
        toast({ title: 'Error', description: data?.message || 'Failed to update location.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed to update location.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = useCallback((loc: LocationRecord) => {
    setLocationToDelete(loc);
    setDeleteDialogOpen(true);
  }, []);

  const handleDelete = async () => {
    if (!locationToDelete) return;
    const id = Number(locationToDelete.ID ?? locationToDelete.Id ?? 0);
    if (!id) return;
    setIsDeleting(true);
    try {
      const payload = { Id: id };
      const apiUrl = `${baseUrl}/api/DeleteLocation`;
      const res = await fetch(apiUrl, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: authToken },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.status === -1) {
        console.log('[DeleteLocation DELETE] API:', apiUrl, 'Payload:', JSON.stringify(payload), 'Response:', data);
        toast({ title: 'Error', description: data?.message || 'Failed to delete location.', variant: 'destructive' });
      } else if (res.ok || data?.status === 0 || data?.status === 1) {
        console.log('[DeleteLocation DELETE] API:', apiUrl, 'Payload:', JSON.stringify(payload), 'Response:', data);
        toast({ title: 'Success', description: 'Location deleted successfully.' });
        setDeleteDialogOpen(false);
        setLocationToDelete(null);
        queryClient.invalidateQueries({ queryKey: ['locations-master'] });
      } else {
        console.log('[DeleteLocation DELETE] API:', apiUrl, 'Payload:', JSON.stringify(payload), 'Response:', data);
        toast({ title: 'Error', description: data?.message || 'Failed to delete location.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed to delete location.', variant: 'destructive' });
    } finally {
      setIsDeleting(false);
    }
  };

  const mobileCards = sorted;

  return (
    <div className="w-full overflow-visible md:max-w-4xl lg:max-w-5xl xl:max-w-none md:mx-auto xl:mx-0 flex flex-col h-screen max-h-screen">
      <div className="lg:hidden flex-shrink-0">
        <AppBar title={locationDisplayName} onBack={onBack} showBackButton />
      </div>

      {/* Mobile: fixed search + Add bar below AppBar */}
      <div className="md:hidden fixed top-14 left-0 right-0 z-40 bg-white border-b border-gray-200 shadow-sm px-4 py-2 flex gap-2 items-center">
        <input
          type="text"
          placeholder={`Search by ${locationFieldLabel}`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 min-w-0 h-10 pl-3 pr-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <Button onClick={openAddModal} className="bg-blue-600 hover:bg-blue-700 shrink-0">
          <Plus className="h-4 w-4 mr-2" />
          Add
        </Button>
      </div>

      <div className="hidden lg:block lg:sticky lg:top-0 lg:z-50 lg:bg-white lg:border-b lg:shadow-sm flex-shrink-0">
        <div className="flex items-center gap-2 mb-2 mt-1 px-3 py-2 lg:px-4 lg:py-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="flex items-center px-2 py-1">
            <ChevronLeft className="h-4 w-4 lg:h-5 lg:w-5" />
            <span className="ml-1 text-sm lg:text-base">Back</span>
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold ml-2">{locationDisplayName} Management</h1>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 px-2 sm:px-4 md:px-6 lg:px-8 overflow-hidden">
        {/* Desktop: Search + Add button + page size (hidden on mobile) */}
        <div className="hidden md:flex flex-col sm:flex-row gap-3 justify-between items-stretch sm:items-center flex-shrink-0">
          <div className="flex flex-1 sm:max-w-md gap-2">
            <input
              type="text"
              placeholder={`Search by ${locationFieldLabel}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 min-w-0 h-10 pl-3 pr-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <Button onClick={openAddModal} className="bg-blue-600 hover:bg-blue-700 shrink-0">
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Show</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-gray-600">entries</span>
          </div>
        </div>

        {/* Data grid / cards */}
        <div className="rounded-md border overflow-hidden bg-white flex-1 min-h-0 flex flex-col mt-4 md:mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
            </div>
          ) : mobileView ? (
            /* Mobile: scrollable cards, no pagination - show all filtered results */
            <div className="flex-1 min-h-0 overflow-y-auto p-4 pt-14 md:pt-4">
              <div className="space-y-4">
              {mobileCards.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No {locationDisplayName.toLowerCase()} found.</p>
              ) : (
                mobileCards.map((loc) => (
                  <Card key={loc.ID ?? loc.Name} className="shadow-sm">
                    <CardContent className="p-4 space-y-2">
                      <p className="font-bold text-gray-900">{loc.Name}</p>
                      <p className="text-sm text-gray-600"><span className="font-medium">{locationTypeLabel}:</span> {(loc.Type ?? loc.TypeDesc ?? loc.LocationType) || '—'}</p>
                      <p className="text-sm text-gray-600"><span className="font-medium">Address:</span> {loc.Address || '—'}</p>
                      <p className="text-sm text-gray-600"><span className="font-medium">Contact:</span> {loc.ContactPerson || '—'}</p>
                      <p className="text-sm text-gray-600"><span className="font-medium">Phone:</span> {(loc.ContactPersonNumber ?? loc.Phone) || '—'}</p>
                      <p className="text-sm text-gray-600"><span className="font-medium">State:</span> {(loc.state ?? loc.State) || '—'}</p>
                      <p className="text-sm text-gray-600"><span className="font-medium">Company:</span> {(loc.CompanyName ?? loc.Company) || '—'}</p>
                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(loc)} className="text-blue-600">
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => confirmDelete(loc)} className="text-red-600">
                          <Trash2 className="h-4 w-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><button type="button" className="font-semibold inline-flex items-center" onClick={() => toggleSort('Name')}>{locationFieldLabel} <SortIcon column="Name" /></button></TableHead>
                  <TableHead><button type="button" className="font-semibold inline-flex items-center" onClick={() => toggleSort('LocationType')}>{locationTypeLabel} <SortIcon column="LocationType" /></button></TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Contact Person</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead><button type="button" className="font-semibold inline-flex items-center" onClick={() => toggleSort('State')}>State <SortIcon column="State" /></button></TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-500 py-8">No {locationDisplayName.toLowerCase()} found.</TableCell>
                  </TableRow>
                ) : (
                  paginated.map((loc) => (
                    <TableRow key={loc.ID ?? loc.Name}>
                      <TableCell className="font-medium">{loc.Name}</TableCell>
                      <TableCell>{(loc.Type ?? loc.TypeDesc ?? loc.LocationType) || '—'}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={loc.Address}>{loc.Address || '—'}</TableCell>
                      <TableCell>{loc.ContactPerson || '—'}</TableCell>
                      <TableCell>{(loc.ContactPersonNumber ?? loc.Phone) || '—'}</TableCell>
                      <TableCell>{(loc.state ?? loc.State) || '—'}</TableCell>
                      <TableCell>{(loc.CompanyName ?? loc.Company) || '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(loc)} className="text-blue-600 hover:bg-blue-50">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => confirmDelete(loc)} className="text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          )}
        </div>

        {/* Pagination - desktop only; mobile shows all cards with no pagination */}
        <div className="hidden md:flex flex-col sm:flex-row justify-between items-center gap-3 flex-shrink-0 mt-4">
          <div className="text-sm text-gray-600">
            Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, sorted.length)} of {sorted.length} entries
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  aria-disabled={currentPage === 1}
                  href="#"
                />
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink isActive={currentPage === page} onClick={() => setCurrentPage(page)} href="#">{page}</PaginationLink>
                    </PaginationItem>
                  );
                }
                if (page === currentPage - 2 || page === currentPage + 2) {
                  return <PaginationItem key={`ellipsis-${page}`}><PaginationEllipsis /></PaginationItem>;
                }
                return null;
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  aria-disabled={currentPage === totalPages}
                  href="#"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>

      {/* Add Location modal - same layout as Edit */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add {locationDisplayName}</DialogTitle>
            <DialogDescription>Enter the {locationFieldLabel.toLowerCase()} details below.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{locationFieldLabel}</Label>
              <Input value={form.Name} onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))} placeholder={locationFieldLabel} />
            </div>
            <div className="grid gap-2">
              <Label>{locationTypeLabel}</Label>
              <Select value={form.LocationType} onValueChange={(v) => setForm((f) => ({ ...f, LocationType: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Address</Label>
              <Textarea value={form.Address} onChange={(e) => setForm((f) => ({ ...f, Address: e.target.value }))} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Contact Person</Label>
                <Input value={form.ContactPerson} onChange={(e) => setForm((f) => ({ ...f, ContactPerson: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Phone</Label>
                <Input type="tel" inputMode="numeric" value={form.Phone} onChange={(e) => { const v = e.target.value; if (v && !validatePhone(v)) return; setForm((f) => ({ ...f, Phone: v })); }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>State</Label>
                <Select value={form.State || (states[0]?.State ?? '')} onValueChange={(v) => setForm((f) => ({ ...f, State: v }))}>
                  <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent>
                    {states.map((s) => (
                      <SelectItem key={s.State} value={s.State}>{s.State}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Company</Label>
                <Select
                  value={form.CompanyID || (companies[0] ? String(companies[0].Id ?? companies[0].CompanyID ?? '') : '')}
                  onValueChange={(v) => {
                    const company = companies.find((c) => String(c.Id ?? c.CompanyID ?? '') === v);
                    setForm((f) => ({ ...f, CompanyID: v, State: company?.State ?? f.State }));
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Company" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.Id ?? c.CompanyID ?? c.Name} value={String(c.Id ?? c.CompanyID ?? '')}>{c.Name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModalOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700">
              {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding...</> : <><Plus className="h-4 w-4 mr-2" /> Add</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit {locationDisplayName}</DialogTitle>
            <DialogDescription>Update {locationFieldLabel.toLowerCase()} details.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{locationFieldLabel}</Label>
              <Input value={form.Name} onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))} placeholder={locationFieldLabel} />
            </div>
            <div className="grid gap-2">
              <Label>{locationTypeLabel}</Label>
              <Select value={form.LocationType} onValueChange={(v) => setForm((f) => ({ ...f, LocationType: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Address</Label>
              <Textarea value={form.Address} onChange={(e) => setForm((f) => ({ ...f, Address: e.target.value }))} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Contact Person</Label>
                <Input value={form.ContactPerson} onChange={(e) => setForm((f) => ({ ...f, ContactPerson: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Phone</Label>
                <Input type="tel" inputMode="numeric" value={form.Phone} onChange={(e) => { const v = e.target.value; if (v && !validatePhone(v)) return; setForm((f) => ({ ...f, Phone: v })); }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>State</Label>
                <Select value={form.State || (states[0]?.State ?? '')} onValueChange={(v) => setForm((f) => ({ ...f, State: v }))}>
                  <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent>
                    {states.map((s) => (
                      <SelectItem key={s.State} value={s.State}>{s.State}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Company</Label>
                <Select
                  value={form.CompanyID || (companies[0] ? String(companies[0].Id ?? companies[0].CompanyID ?? '') : '')}
                  onValueChange={(v) => {
                    const company = companies.find((c) => String(c.Id ?? c.CompanyID ?? '') === v);
                    setForm((f) => ({ ...f, CompanyID: v, State: company?.State ?? f.State }));
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Company" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.Id ?? c.CompanyID ?? c.Name} value={String(c.Id ?? c.CompanyID ?? '')}>{c.Name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={isSubmitting}>
              {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</> : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Location</DialogTitle>
            <DialogDescription>Are you sure you want to delete &quot;{locationToDelete?.Name}&quot;? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setLocationToDelete(null); }} disabled={isDeleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting...</> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
